import { Button, Input } from '@cloudflare/kumo';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { cleanId } from '../../shared/clean-id.ts';
import { api, queryClient, unwrap } from '../api.ts';
import { ErrorNotice } from '../common.tsx';
export function TransferInitiation({ siteId }: { siteId: string }) {
  const [email, setEmail] = useState('');
  const request = useRef<{ email: string; id: string } | null>(null);
  const transfers = useQuery({
    queryKey: ['site-transfers', siteId],
    queryFn: async () =>
      unwrap(await api.GET('/api/sites/{siteId}/transfers', { params: { path: { siteId } } })),
  });
  const send = useMutation({
    mutationFn: async () => {
      const recipient = email.trim().toLowerCase();
      if (request.current?.email !== recipient) request.current = { email: recipient, id: cleanId() };
      return unwrap(
        await api.POST('/api/sites/{siteId}/transfers', {
          params: { path: { siteId } },
          body: { email: recipient, transferId: request.current.id },
        }),
      );
    },
    onSuccess: async () => {
      setEmail('');
      request.current = null;
      await queryClient.invalidateQueries({ queryKey: ['site-transfers', siteId] });
    },
  });
  return (
    <details className="border-t border-t-kumo-line mt-8 pt-6 [&_summary]:font-semibold [&_p]:mx-0 [&_p]:mt-3 [&_p]:mb-0 [&_p]:leading-[1.65]">
      <summary className="cursor-pointer">Transfer ownership</summary>
      <p className="leading-[1.65]">
        Move this site to another customer account. You stay owner until they accept and any billing setup
        succeeds. Site collaborators keep access.
      </p>
      <ErrorNotice error={send.error ?? transfers.error} />
      <form
        className="mx-0 my-6 flex flex-col items-stretch gap-3 [&_>_:first-child]:flex-1 min-[601px]:flex-row min-[601px]:items-end"
        onSubmit={(event) => {
          event.preventDefault();
          send.mutate();
        }}
      >
        <Input
          label="New owner’s email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <Button type="submit" disabled={send.isPending}>
          Send transfer request
        </Button>
      </form>
      {transfers.data?.map((transfer) => (
        <p className="leading-[1.65]" key={transfer.id}>
          <Link to="/transfers/$transferId" params={{ transferId: transfer.id }}>
            {transfer.toEmail} · {transfer.status.replaceAll('_', ' ')}
          </Link>
        </p>
      ))}
    </details>
  );
}
